/**
 * マークダウンを正規化する
 * - 各行の先頭の不要なインデントを削除（コードブロックとして認識されるのを防ぐ）
 * - セル内改行を含むテーブルを正しい形式に変換（改行を<br>に置換）
 * - 1行に圧縮されたテーブルを正しい形式に変換
 * - HTML テーブルのセル内のバッククォートを <code> に変換（HTML ブロック内は Markdown が解釈されないため）
 */
export const normalizeMarkdownTable = (markdown: string): string => {
	let inCodeBlock = false;

	// 1. インデントの正規化
	const indentNormalized = markdown
		.split("\n")
		.map((line) => {
			// コードブロック（```）の内部かどうかを追跡
			if (line.trim().startsWith("```")) {
				inCodeBlock = !inCodeBlock;
				return line;
			}

			// コードブロック内は変更しない
			if (inCodeBlock) {
				return line;
			}

			// 4スペース以上のインデントがある行から先頭の空白を削除（コードブロックとして認識されるのを防ぐ）
			// ただし、リストのネスト（-, *, +, 1. など）は意図的なインデントなので保持する
			const trimmedLine = line.trimStart();

			// 空行はそのまま
			if (trimmedLine.length === 0) {
				return line;
			}

			// リスト項目（-, *, +, 1. など）はインデントを保持（ネスト構造のため）
			if (/^[-*+]\s/.test(trimmedLine) || /^\d+\.\s/.test(trimmedLine)) {
				return line;
			}

			// 見出し、テーブル、通常テキストなどの行は先頭空白を削除
			if (
				/^[#|]/.test(trimmedLine) || // 見出し、テーブル
				/^[^\s]/.test(trimmedLine) // 通常のテキスト
			) {
				const leadingSpaces = line.length - trimmedLine.length;
				// 4スペース以上のインデントはコードブロックになるので削除
				if (leadingSpaces >= 4) {
					return trimmedLine;
				}
			}

			return line;
		})
		.join("\n");

	// 2. セル内改行の処理（テーブル行の途中で改行されているケースを結合）
	// テーブル行は | で始まり | で終わる必要があるが、セル内に改行があると複数行にまたがる
	inCodeBlock = false;
	const lines = indentNormalized.split("\n");
	const cellLinesMerged: string[] = [];
	let pendingTableRow = ""; // 未完了のテーブル行を蓄積

	for (const line of lines) {
		const trimmed = line.trim();

		// コードブロックの追跡
		if (trimmed.startsWith("```")) {
			if (pendingTableRow) {
				cellLinesMerged.push(pendingTableRow);
				pendingTableRow = "";
			}
			inCodeBlock = !inCodeBlock;
			cellLinesMerged.push(line);
			continue;
		}

		if (inCodeBlock) {
			cellLinesMerged.push(line);
			continue;
		}

		const startsWithPipe = trimmed.startsWith("|");
		const endsWithPipe = trimmed.endsWith("|");
		const hasPipe = trimmed.includes("|");

		if (pendingTableRow) {
			// 複数行にまたがるテーブル行を収集中
			if (endsWithPipe) {
				// この行で行が完結する
				pendingTableRow += "<br>" + trimmed;
				cellLinesMerged.push(pendingTableRow);
				pendingTableRow = "";
			} else if (hasPipe || trimmed.length > 0) {
				// パイプがあるか、空でないコンテンツがある場合は続きとして収集
				pendingTableRow += "<br>" + trimmed;
			} else {
				// 空行 - テーブルが終了した可能性
				cellLinesMerged.push(pendingTableRow);
				pendingTableRow = "";
				cellLinesMerged.push(line);
			}
		} else if (startsWithPipe && !endsWithPipe && hasPipe) {
			// | で始まるが | で終わらない行 = 複数行テーブル行の開始
			pendingTableRow = trimmed;
		} else {
			// 通常の行
			cellLinesMerged.push(line);
		}
	}

	// 未処理のテーブル行があれば追加
	if (pendingTableRow) {
		cellLinesMerged.push(pendingTableRow);
	}

	const result = cellLinesMerged.join("\n");

	// 3. テーブルの正規化（1行に圧縮されたテーブルを分割）
	const separatorPattern = /\|\s*[-:]+\s*\|/;

	const tableNormalized = result
		.split("\n")
		.map((line) => {
			// 区切り行を含む行かつ、複数の行が1行に圧縮されている場合
			if (separatorPattern.test(line) && (line.match(/\|/g) || []).length > 4) {
				// 行の境界にある "| |" を改行に置換
				return line.replace(/([^|])\s*\|\s+\|/g, "$1 |\n|");
			}
			return line;
		})
		.join("\n");

	// 4. HTML テーブルのセル内のインラインコード（`...`）を <code> に変換
	return tableNormalized.replace(/<(td|th)\b([^>]*)>([\s\S]*?)<\/\1>/gi, (_, tag, attrs, cell) => {
		return `<${tag}${attrs}>${cell.replace(/`([^`\n]+)`/g, "<code>$1</code>")}</${tag}>`;
	});
};
